/**
 * Session-scoped existence cache: for each UILM module, the set of key
 * names that already exist on UILM. Used to decide whether a save needs
 * to be preceded by a `Key/GetsByKeyNames` lookup.
 *
 * Why this exists:
 *   - When a key is NEW, there is no remote DE/etc. resource to preserve,
 *     and no remote EN to compare against — we can save with
 *     `isNewKey: true` and a single-EN `resources[]` without ever
 *     fetching. Cuts per-save API calls from 2 → 1.
 *   - When a key is EXISTING, we still need to fetch to merge non-EN
 *     resources (otherwise EN-only saves wipe DE) and to skip no-ops.
 *
 * Population: one `GetUilmFile` call per module per session, on first
 * use. `getUilmFile` returns the published EN file as a flat
 * `{KEY: value}` object — `Object.keys()` gives the existence set.
 *
 * Staleness considerations:
 *   - The cache is session-scoped (cleared on page reload). Cross-tab
 *     edits are NOT propagated.
 *   - If UILM rejects a save because our existence guess was wrong
 *     (e.g. key existed but was unpublished and not in the file, or got
 *     deleted by another actor), `invalidateExistence(moduleId)` should
 *     be called and the operation retried. Currently the sync helpers
 *     soft-fail on UILM errors, so callers can ignore — but the cache
 *     can be invalidated explicitly when an admin action is known to
 *     diverge from cache (orphan delete, seed, etc.).
 */

import { getUilmFile } from '@/components/core/language-selector/services/language.service';
import { DEFAULT_LANGUAGE_CULTURE } from './uilm-constants';

const existenceByModule = new Map<string, Set<string>>();
const inflightLoads = new Map<string, Promise<void>>();

/**
 * Ensure the existence set for `moduleId` is loaded. Idempotent: a second
 * call returns immediately; concurrent callers share one network round
 * trip via the `inflightLoads` map.
 *
 * The promise resolves with no value — read state via `isKnownKey` after
 * awaiting. We don't return the set because callers should not hold a
 * reference to the internal set (mutations would corrupt the cache).
 *
 * If the underlying `getUilmFile` request fails (e.g. module exists but
 * has never been published, so there's no file yet), the cache is
 * populated with an empty set. That's the correct semantics — no keys
 * exist on UILM yet, every save is `isNewKey: true`.
 */
export async function ensureModuleExistenceLoaded(
  moduleId: string,
  moduleName: string,
): Promise<void> {
  if (existenceByModule.has(moduleId)) return;

  const inflight = inflightLoads.get(moduleId);
  if (inflight) return inflight;

  const load = (async () => {
    try {
      const file = await getUilmFile({
        language: DEFAULT_LANGUAGE_CULTURE,
        moduleName,
      });
      const keyNames =
        file && typeof file === 'object' && !Array.isArray(file) ? Object.keys(file) : [];
      existenceByModule.set(moduleId, new Set(keyNames));
    } catch {
      // Empty file or 404 → no keys yet. Cache as empty so subsequent
      // saves take the "new key" fast path without re-attempting the load.
      existenceByModule.set(moduleId, new Set());
    } finally {
      inflightLoads.delete(moduleId);
    }
  })();

  inflightLoads.set(moduleId, load);
  return load;
}

/**
 * Is this key already on UILM (as of the last cache load)? Returns
 * `false` for unknown modules so the caller falls through the safe path
 * (treat as new). Callers should call `ensureModuleExistenceLoaded`
 * before consulting this, otherwise unknown-module always returns false.
 */
export function isKnownKey(moduleId: string, keyName: string): boolean {
  return existenceByModule.get(moduleId)?.has(keyName) ?? false;
}

/** Mark a key as existing after a successful save. */
export function markKeyAsKnown(moduleId: string, keyName: string): void {
  const set = existenceByModule.get(moduleId);
  if (set) {
    set.add(keyName);
  } else {
    existenceByModule.set(moduleId, new Set([keyName]));
  }
}

/** Mark a key as deleted (e.g. orphan delete from the Translations Tab). */
export function markKeyAsDeleted(moduleId: string, keyName: string): void {
  existenceByModule.get(moduleId)?.delete(keyName);
}

/**
 * Drop a module's cached set. Use after an action that may have
 * diverged from cache (e.g. UILM rejected a save, suggesting the
 * existence flag was wrong) — the next `ensureModuleExistenceLoaded`
 * will refetch.
 */
export function invalidateExistence(moduleId?: string): void {
  if (moduleId) {
    existenceByModule.delete(moduleId);
    inflightLoads.delete(moduleId);
  } else {
    existenceByModule.clear();
    inflightLoads.clear();
  }
}
